import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ALL_EXPEDIENT_STATUSES,
  FINALIZED_STATUSES,
  STATUSES_WITH_ACTIVE_MANAGEMENT,
  expedientStatusLabel,
} from '@claimup/shared/constants/expedient';
import api, { getErrorMessage } from '../../../services/api';

/**
 * Envío masivo de SMS: elegir expedientes, escribir el mensaje, ver lo que va a
 * pasar y enviarlo.
 *
 * Lo que la pantalla NO decide: a quién se excluye siempre (judiciales,
 * cerrados, sin móvil válido, números repetidos), el tope de 160 con los datos
 * de cada deudor y si alcanza el saldo. Lo decide el servidor al calcular, y se
 * enseña aquí. Ver docs/sms-masivo-plan.md.
 *
 * 🔴 El cálculo caduca en cuanto se toca algo: enviar manda el número de
 * destinatarios que se vio, y si la selección ha cambiado el servidor lo rechaza.
 */

const ESTADOS_ELEGIBLES = ALL_EXPEDIENT_STATUSES.filter(e => !FINALIZED_STATUSES.includes(e));
const VARIABLES = [
  { clave: '{nombre}', ayuda: 'Nombre de pila del deudor' },
  { clave: '{importe}', ayuda: 'Importe pendiente, con céntimos' },
  { clave: '{acreedor}', ayuda: 'Nombre comercial del expediente' },
];
const MAX = 160;

const ESTADO_CAMPANA = {
  running: { texto: 'Enviando', clase: 'bg-blue-50 text-blue-700' },
  stopping: { texto: 'Parando…', clase: 'bg-amber-50 text-amber-700' },
  stopped: { texto: 'Parada', clase: 'bg-amber-50 text-amber-700' },
  interrupted: { texto: 'Interrumpida', clase: 'bg-red-50 text-red-700' },
  done: { texto: 'Terminada', clase: 'bg-green-50 text-green-700' },
};

const MOTIVOS_EXCLUSION = {
  sin_telefono: 'sin teléfono',
  telefono_no_valido: 'sin móvil válido (fijo o número de relleno)',
  numero_repetido: 'número repetido en otro expediente',
};

const euros = n => (typeof n === 'number' ? n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—');

const fechaHora = f =>
  f ? new Date(f).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const input = 'w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5';
const etiqueta = 'block text-xs font-medium text-slate-600 mb-1';
const boton =
  'px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-900 disabled:opacity-50';
const botonClaro =
  'px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50';

const Bloque = ({ titulo, children }) => (
  <div className='bg-white rounded-lg border border-slate-200'>
    <div className='px-6 py-3 border-b border-slate-200'>
      <h3 className='text-sm font-semibold text-slate-900'>{titulo}</h3>
    </div>
    <div className='p-6 space-y-4'>{children}</div>
  </div>
);

const Calculo = ({ r }) => (
  <div className='space-y-3'>
    <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
      <div className='rounded-lg border border-slate-200 px-4 py-3'>
        <p className='text-xs text-slate-500 uppercase'>Destinatarios</p>
        <p className='text-xl font-semibold text-slate-900'>{r.destinatarios}</p>
        <p className='text-xs text-slate-500'>de {r.candidatos} expedientes</p>
      </div>
      <div className='rounded-lg border border-slate-200 px-4 py-3'>
        <p className='text-xs text-slate-500 uppercase'>Coste estimado</p>
        <p className='text-xl font-semibold text-slate-900'>{euros(r.coste_estimado_eur)}</p>
        {r.fuera_de_espana > 0 && <p className='text-xs text-amber-700'>{r.fuera_de_espana} fuera de España</p>}
      </div>
      <div className='rounded-lg border border-slate-200 px-4 py-3'>
        <p className='text-xs text-slate-500 uppercase'>Saldo</p>
        <p className='text-xl font-semibold text-slate-900'>{r.saldo?.error ? '—' : euros(r.saldo?.euros)}</p>
        {r.saldo?.error && <p className='text-xs text-red-700'>No se pudo consultar</p>}
      </div>
      <div className='rounded-lg border border-slate-200 px-4 py-3'>
        <p className='text-xs text-slate-500 uppercase'>Mensaje más largo</p>
        <p className={`text-xl font-semibold ${r.mas_largo?.longitud > MAX ? 'text-red-700' : 'text-slate-900'}`}>
          {r.mas_largo ? `${r.mas_largo.longitud}/${MAX}` : '—'}
        </p>
      </div>
    </div>

    {Object.entries(r.excluidos || {}).some(([, n]) => n > 0) && (
      <p className='text-xs text-slate-600'>
        Se quedan fuera:{' '}
        {Object.entries(r.excluidos)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${n} ${MOTIVOS_EXCLUSION[k] || k}`)
          .join(' · ')}
        . Los judiciales y los cerrados no entran nunca.
      </p>
    )}

    {r.caracteres_raros?.length > 0 && (
      <p className='text-xs text-amber-700'>
        Hay caracteres que no caben en un SMS normal ({r.caracteres_raros.join(' ')}): BulkGate los cambia o no se ven
        bien. Mejor quitarlos (las tildes en á, í, ó y ú, los emojis…).
      </p>
    )}

    {r.bloqueos?.length > 0 && (
      <div className='rounded-lg bg-red-50 border border-red-200 px-3 py-2 space-y-1'>
        {r.bloqueos.map(b => (
          <p key={b} className='text-sm text-red-800 flex gap-2'>
            <ExclamationTriangleIcon className='w-5 h-5 flex-shrink-0' />
            {b}
          </p>
        ))}
      </div>
    )}

    {r.muestras?.length > 0 && (
      <div>
        <p className={etiqueta}>Así le llega a algunos</p>
        <ul className='space-y-2'>
          {r.muestras.map(m => (
            <li key={m.expedient_id} className='rounded-lg bg-slate-50 px-3 py-2'>
              <p className='text-xs text-slate-500'>
                <Link to={`/view/${m.expedient_id}`} className='text-indigo-700 hover:underline'>
                  {m.deudor || 'Ver expediente'}
                </Link>{' '}
                · +{m.telefono} · {m.longitud}/{MAX}
              </p>
              <p className='text-sm text-slate-800'>{m.texto}</p>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const Campana = ({ c, onParar, onContinuar, ocupado }) => {
  const [abierta, setAbierta] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const estado = ESTADO_CAMPANA[c.estado] || { texto: c.estado, clase: 'bg-slate-100 text-slate-700' };
  const hechos = c.total - c.pendientes;
  const pct = c.total ? Math.round((hechos / c.total) * 100) : 0;
  const problemas = c.rechazados + c.errores + c.saltados;

  const abrir = async () => {
    if (abierta) return setAbierta(false);
    setAbierta(true);
    try {
      const { data } = await api.get(`/sms/bulk/${c.id}`);
      setDetalle(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    return null;
  };

  return (
    <li className='py-3'>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0'>
          <p className='text-sm font-medium text-slate-900'>
            {c.nombre} <span className={`ml-1 text-xs px-2 py-0.5 rounded-lg ${estado.clase}`}>{estado.texto}</span>
          </p>
          <p className='text-xs text-slate-500 mt-0.5'>
            {fechaHora(c.creada)} · {c.enviados} enviados de {c.total}
            {problemas > 0 && ` · ${problemas} sin enviar`} · coste estimado {euros(c.coste_estimado_eur)}
          </p>
          <div className='mt-1.5 h-1.5 w-64 max-w-full bg-slate-100 rounded-lg overflow-hidden'>
            <div className='h-full bg-blue-500' style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className='flex gap-2 flex-shrink-0'>
          {c.estado === 'running' && (
            <button type='button' className={botonClaro} disabled={ocupado} onClick={() => onParar(c)}>
              Parar
            </button>
          )}
          {['stopped', 'interrupted'].includes(c.estado) && c.pendientes + c.errores > 0 && (
            <button type='button' className={botonClaro} disabled={ocupado} onClick={() => onContinuar(c)}>
              Continuar
            </button>
          )}
          <button type='button' className='text-xs text-slate-600 hover:underline' onClick={abrir}>
            {abierta ? 'Ocultar' : 'Detalle'}
          </button>
        </div>
      </div>
      {abierta && (
        <div className='mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs space-y-1'>
          <p className='text-slate-700'>
            <span className='font-medium'>Mensaje:</span> {c.plantilla}
          </p>
          {!detalle ? (
            <p className='text-slate-500'>Cargando…</p>
          ) : detalle.problemas.length === 0 ? (
            <p className='text-slate-500'>Ningún destinatario con problemas.</p>
          ) : (
            <ul className='space-y-0.5'>
              {detalle.problemas.map(p => (
                <li key={p.expedient_id} className='text-slate-700'>
                  <Link to={`/view/${p.expedient_id}`} className='text-indigo-700 hover:underline'>
                    +{p.telefono}
                  </Link>{' '}
                  · {p.estado === 'rejected' ? 'rechazado' : p.estado === 'skipped' ? 'saltado' : 'error'}: {p.motivo}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

const SmsMasivo = () => {
  const [clientes, setClientes] = useState([]);
  const [elegidos, setElegidos] = useState([]);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [minimo, setMinimo] = useState('');
  const [maximo, setMaximo] = useState('');
  const [estados, setEstados] = useState([...STATUSES_WITH_ACTIVE_MANAGEMENT]);
  const [sinPrimerContacto, setSinPrimerContacto] = useState(false);
  const [sinSmsPrevio, setSinSmsPrevio] = useState(false);
  const [plantilla, setPlantilla] = useState('');
  const [nombre, setNombre] = useState('');
  const [calculo, setCalculo] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [campanas, setCampanas] = useState([]);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    api
      .get('/portfolio-performance/clients')
      .then(({ data }) => setClientes(data.data?.clientes || []))
      .catch(err => toast.error(`No se pudieron cargar los clientes: ${getErrorMessage(err)}`));
  }, []);

  const cargarCampanas = useCallback(async () => {
    try {
      const { data } = await api.get('/sms/bulk');
      setCampanas(data.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    cargarCampanas();
  }, [cargarCampanas]);

  // Mientras alguna se está enviando, el progreso se refresca solo.
  const enMarcha = campanas.some(c => ['running', 'stopping'].includes(c.estado));
  useEffect(() => {
    if (!enMarcha) return undefined;
    const t = setInterval(cargarCampanas, 3000);
    return () => clearInterval(t);
  }, [enMarcha, cargarCampanas]);

  const filtros = useMemo(() => {
    const f = {
      client_ids: elegidos,
      estados,
      without_first_contact: sinPrimerContacto,
      without_previous_sms: sinSmsPrevio,
    };
    if (desde) f.imported_from = desde;
    if (hasta) f.imported_to = hasta;
    if (minimo !== '') f.amount_min = Number(minimo);
    if (maximo !== '') f.amount_max = Number(maximo);
    return f;
  }, [elegidos, estados, sinPrimerContacto, sinSmsPrevio, desde, hasta, minimo, maximo]);

  // 🔴 Cualquier cambio deja el cálculo viejo: lo que se envía tiene que ser lo que se vio.
  useEffect(() => {
    setCalculo(null);
  }, [filtros, plantilla]);

  const nombreDe = id => clientes.find(c => c.id === id)?.nombre || id;

  const calcular = async () => {
    setCalculando(true);
    try {
      const { data } = await api.post('/sms/bulk/preview', { filters: filtros, template: plantilla });
      setCalculo(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err), { autoClose: 8000 });
    } finally {
      setCalculando(false);
    }
  };

  const enviar = async () => {
    // eslint-disable-next-line no-alert
    const seguro = window.confirm(
      `Vas a enviar ${calculo.destinatarios} SMS (unos ${euros(calculo.coste_estimado_eur)}).\n\n` +
        'Llegan a personas y no se pueden deshacer. ¿Enviar?'
    );
    if (!seguro) return;
    setEnviando(true);
    try {
      await api.post('/sms/bulk', {
        filters: filtros,
        template: plantilla,
        name: nombre.trim(),
        expected: calculo.destinatarios,
      });
      toast.success('Envío en marcha. El progreso se ve abajo.');
      setCalculo(null);
      await cargarCampanas();
    } catch (err) {
      toast.error(getErrorMessage(err), { autoClose: 10000 });
    } finally {
      setEnviando(false);
    }
  };

  const accion = async ruta => {
    setOcupado(true);
    try {
      await api.post(ruta);
      await cargarCampanas();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setOcupado(false);
    }
  };

  const insertar = v => setPlantilla(p => `${p}${p && !p.endsWith(' ') ? ' ' : ''}${v}`);
  const largoPlantilla = plantilla.length;
  const puedeCalcular = elegidos.length > 0 && estados.length > 0 && plantilla.trim();

  return (
    <div className='space-y-6'>
      <Bloque titulo='1. A quién'>
        <div>
          <label className={etiqueta} htmlFor='sms-cliente'>
            Cliente
          </label>
          <select
            id='sms-cliente'
            className={input}
            value=''
            onChange={e => e.target.value && setElegidos(l => [...new Set([...l, e.target.value])])}
          >
            <option value=''>Añadir un cliente…</option>
            {clientes
              .filter(c => !elegidos.includes(c.id))
              .map(c => (
                <option key={c.id} value={c.id}>
                  {c.nombre} ({c.expedientes})
                </option>
              ))}
          </select>
          {elegidos.length > 0 && (
            <div className='flex flex-wrap gap-2 mt-2'>
              {elegidos.map(id => (
                <span key={id} className='inline-flex items-center gap-1 text-xs bg-slate-100 rounded-lg px-2 py-1'>
                  {nombreDe(id)}
                  <button
                    type='button'
                    aria-label={`Quitar ${nombreDe(id)}`}
                    onClick={() => setElegidos(l => l.filter(x => x !== id))}
                  >
                    <XMarkIcon className='w-3.5 h-3.5' />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
          <div>
            <label className={etiqueta} htmlFor='sms-desde'>
              Importados desde
            </label>
            <input
              id='sms-desde'
              type='date'
              className={input}
              value={desde}
              onChange={e => setDesde(e.target.value)}
            />
          </div>
          <div>
            <label className={etiqueta} htmlFor='sms-hasta'>
              Importados hasta
            </label>
            <input
              id='sms-hasta'
              type='date'
              className={input}
              value={hasta}
              onChange={e => setHasta(e.target.value)}
            />
          </div>
          <div>
            <label className={etiqueta} htmlFor='sms-min'>
              Importe mínimo (€)
            </label>
            <input
              id='sms-min'
              type='number'
              min='0'
              step='0.01'
              className={input}
              value={minimo}
              onChange={e => setMinimo(e.target.value)}
            />
          </div>
          <div>
            <label className={etiqueta} htmlFor='sms-max'>
              Importe máximo (€)
            </label>
            <input
              id='sms-max'
              type='number'
              min='0'
              step='0.01'
              className={input}
              value={maximo}
              onChange={e => setMaximo(e.target.value)}
            />
          </div>
        </div>

        <div>
          <p className={etiqueta}>Estados</p>
          <div className='flex flex-wrap gap-x-4 gap-y-1'>
            {ESTADOS_ELEGIBLES.map(e => (
              <label key={e} className='inline-flex items-center gap-1.5 text-sm text-slate-700'>
                <input
                  type='checkbox'
                  className='rounded-lg'
                  checked={estados.includes(e)}
                  onChange={ev => setEstados(l => (ev.target.checked ? [...l, e] : l.filter(x => x !== e)))}
                />
                {expedientStatusLabel(e)}
              </label>
            ))}
          </div>
        </div>

        <div className='space-y-1'>
          <label className='flex items-center gap-2 text-sm text-slate-700'>
            <input
              type='checkbox'
              className='rounded-lg'
              checked={sinPrimerContacto}
              onChange={e => setSinPrimerContacto(e.target.checked)}
            />
            Solo los que no tienen tarea de primer contacto (ni SMS ni WhatsApp inicial)
          </label>
          <label className='flex items-center gap-2 text-sm text-slate-700'>
            <input
              type='checkbox'
              className='rounded-lg'
              checked={sinSmsPrevio}
              onChange={e => setSinSmsPrevio(e.target.checked)}
            />
            Solo los que nunca han recibido un SMS
          </label>
        </div>
      </Bloque>

      <Bloque titulo='2. El mensaje'>
        <div>
          <div className='flex items-center justify-between mb-1'>
            <label className={etiqueta} htmlFor='sms-plantilla'>
              Texto
            </label>
            <div className='flex gap-1'>
              {VARIABLES.map(v => (
                <button
                  key={v.clave}
                  type='button'
                  title={v.ayuda}
                  onClick={() => insertar(v.clave)}
                  className='text-xs px-2 py-0.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200'
                >
                  {v.clave}
                </button>
              ))}
            </div>
          </div>
          <textarea
            id='sms-plantilla'
            rows={3}
            className={input}
            value={plantilla}
            onChange={e => setPlantilla(e.target.value)}
            placeholder='Hola {nombre}, tienes {importe} pendientes con {acreedor}. Escríbenos por WhatsApp al…'
          />
          <p className='text-[11px] text-slate-500 mt-1'>
            {largoPlantilla} caracteres con las variables sin rellenar. El tope de {MAX} se comprueba con los datos de
            cada deudor al calcular.
          </p>
        </div>
        <div>
          <label className={etiqueta} htmlFor='sms-nombre'>
            Nombre del envío (para reconocerlo después)
          </label>
          <input
            id='sms-nombre'
            className={input}
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            placeholder='TaxDown < 100 € · tanda 1'
          />
        </div>
      </Bloque>

      <Bloque titulo='3. Comprobar y enviar'>
        <div className='flex gap-2'>
          <button type='button' className={botonClaro} disabled={!puedeCalcular || calculando} onClick={calcular}>
            {calculando ? 'Calculando…' : 'Calcular'}
          </button>
          <button
            type='button'
            className={boton}
            disabled={!calculo?.puede_enviar || !nombre.trim() || enviando}
            onClick={enviar}
          >
            {calculo?.puede_enviar ? `Enviar ${calculo.destinatarios} SMS` : 'Enviar'}
          </button>
        </div>
        {!calculo && (
          <p className='text-xs text-slate-500'>Calcula antes de enviar: así ves a quién llega y cómo queda.</p>
        )}
        {calculo && <Calculo r={calculo} />}
        {calculo?.puede_enviar && !nombre.trim() && (
          <p className='text-xs text-amber-700'>Ponle un nombre al envío para poder enviarlo.</p>
        )}
      </Bloque>

      <Bloque titulo='Envíos'>
        {campanas.length === 0 ? (
          <p className='text-sm text-slate-500'>Todavía no se ha hecho ningún envío masivo.</p>
        ) : (
          <ul className='divide-y divide-slate-100'>
            {campanas.map(c => (
              <Campana
                key={c.id}
                c={c}
                ocupado={ocupado}
                onParar={x => accion(`/sms/bulk/${x.id}/stop`)}
                onContinuar={x => accion(`/sms/bulk/${x.id}/resume`)}
              />
            ))}
          </ul>
        )}
      </Bloque>
    </div>
  );
};

export default SmsMasivo;
